/**
 * §P.5 fase 2 · Los tipos y las reglas de los exámenes de grado.
 *
 * Se llama `tiposDeExamen.ts` y no `examenes.ts` porque en macOS el sistema de
 * ficheros no distingue mayúsculas: `examenes.ts` y `Examenes.tsx` son el mismo
 * nombre para él, y TypeScript se niega —con razón— a cargar los dos.
 *
 * ## Nace en borrador, y eso separa dos cosas distintas
 *
 * «Es lo que separa *estoy mirando fechas* de *esto va a ocurrir*: mientras es
 * borrador no se puede inscribir a nadie». Los cinco estados no son adornos: son el
 * ciclo entero de una convocatoria, y de ellos depende qué se puede hacer.
 *
 * ## La rúbrica cuelga del **grado**, no del examen
 *
 * «Poomsae de amarillo» se puntúa igual en diciembre y en junio, así que el `PUT`
 * escribe sobre los grados destino de los inscritos. Consecuencia que la pantalla
 * tiene que decir en voz alta: **hay que inscribir antes de poder definir la
 * rúbrica**. Un formulario vacío sin explicar eso es un formulario que parece roto.
 *
 * Y nunca toca las plantillas globales —las comparten doscientos clubes—: escribe las
 * del club, que a partir de ahí sustituyen a las globales de esos grados. Por eso
 * `is_own: false` se lee y no se edita.
 *
 * ## La nota es un porcentaje del máximo ponderado, no una suma
 *
 * «Así el listón no se mueve solo al añadir un criterio». Es la clase de cosa que hay
 * que explicar donde se define la rúbrica, porque el maestro que añade un criterio
 * nuevo espera que la nota baje y no es lo que pasa.
 *
 * ## Los que no cumplen **también vienen**
 *
 * `v_exam_eligibility` devuelve `is_eligible: false` junto a los demás, y esconderlos
 * sería el error: «obligaría al maestro a salir de la pantalla para inscribir al niño
 * que estuvo dos meses escayolado, que es justo el caso que la vista no sabe juzgar».
 */

import { fechaYHora } from '@/shared/ui/formato';

export type EstadoDeExamen =
  'draft' | 'open' | 'closed' | 'graded' | 'cancelled';

export const ESTADOS_DE_EXAMEN: Record<
  EstadoDeExamen,
  { etiqueta: string; clase: string; explicacion: string }
> = {
  draft: {
    etiqueta: 'Borrador',
    clase: 'pastilla-paused',
    explicacion:
      'Todavía no va a ocurrir: no se puede inscribir a nadie. Ábrelo cuando la fecha esté decidida.',
  },
  open: {
    etiqueta: 'Abierto',
    clase: 'pastilla-bien',
    explicacion: 'Se puede inscribir y retirar candidatos.',
  },
  closed: {
    etiqueta: 'Cerrado',
    clase: 'pastilla-info',
    explicacion: 'La lista está hecha. Queda examinar y poner las notas.',
  },
  graded: {
    etiqueta: 'Calificado',
    clase: 'pastilla-bien',
    explicacion: 'Con notas. De aquí salen los grados nuevos y los diplomas.',
  },
  cancelled: {
    etiqueta: 'Cancelado',
    clase: 'pastilla-mal',
    explicacion: 'No se celebró. Se queda para que el histórico se explique.',
  },
};

export const etiquetaDeEstadoDeExamen = (estado: string): string =>
  ESTADOS_DE_EXAMEN[estado as EstadoDeExamen]?.etiqueta ?? estado;

export const pastillaDeExamen = (estado: string): string =>
  ESTADOS_DE_EXAMEN[estado as EstadoDeExamen]?.clase ?? 'pastilla';

/**
 * **Solo se inscribe en un examen abierto.**
 *
 * Y no es cortesía de la interfaz: en borrador el servidor lo rechaza, porque un
 * examen que todavía es una idea no puede tener gente apuntada.
 */
export const admiteInscripciones = (estado: string): boolean =>
  estado === 'open';

/** La rúbrica se define mientras el examen no esté calificado ni cancelado. */
export const admiteRubrica = (estado: string): boolean =>
  estado === 'open' || estado === 'closed' || estado === 'draft';

export interface Examen {
  readonly id: string;
  readonly discipline_id: string;
  readonly discipline_name: string;
  readonly venue_id: string | null;
  readonly venue_name: string | null;
  readonly name: string;
  readonly scheduled_at: Date | string;
  readonly registration_deadline: Date | string | null;
  /** **Céntimos enteros.** 15,00 € es `1500`. */
  readonly fee_cents: number | null;
  readonly status: EstadoDeExamen;
  readonly examiner_user_id: string | null;
  readonly examiner_name: string | null;
  readonly notes: string | null;
  /** **Kiap no aloja ninguna foto del examen**: aquí solo queda el enlace del club. */
  readonly external_album_url: string | null;
  readonly registered_count: number;
  readonly graded_count: number;
  readonly created_at: Date | string;
  readonly updated_at: Date | string;
}

export interface CriterioDeExamen {
  readonly id: string;
  readonly belt_rank_id: string | null;
  readonly rank_name: string | null;
  readonly code: string;
  readonly label: string;
  readonly category: string | null;
  readonly max_score: number;
  /** Cuánto pesa frente a los demás. Ver la cabecera: la nota es un porcentaje. */
  readonly weight: number;
  readonly sort_order: number;
  /** `false` es plantilla global: se lee, no se edita. */
  readonly is_own: boolean;
}

export interface InscripcionDeExamen {
  readonly id: string;
  readonly exam_event_id: string;
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly photo_asset_id: string | null;
  readonly from_rank_id: string | null;
  readonly from_rank_name: string | null;
  readonly to_rank_id: string;
  readonly to_rank_name: string;
  readonly status: string;
  /** Carga opaca: se guarda para enseñarla. Ver `resumenDeRequisitos`. */
  readonly eligibility_snapshot: Readonly<Record<string, unknown>> | null;
  readonly result: string | null;
  readonly overall_score: number | null;
  readonly fee_charged_cents: number | null;
  readonly created_at: Date | string;
}

export interface CandidatoPosible {
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly current_rank_id: string | null;
  readonly current_rank_name: string | null;
  readonly next_rank_id: string | null;
  readonly next_rank_name: string | null;
  readonly attendance_rate: number;
  readonly months_since_last: number;
  readonly is_first_exam: boolean;
  readonly is_eligible: boolean;
  readonly already_registered: boolean;
}

/**
 * Por qué un candidato no cumple, en palabras.
 *
 * La vista devuelve `is_eligible: false` sin decir el motivo, así que se deduce de las
 * cifras que sí trae. **No se usa para decidir nada** —decide el servidor—: se usa
 * para que el maestro sepa qué le falta al niño antes de inscribirlo de todas formas.
 */
export function porQueNoCumple(c: CandidatoPosible): string | null {
  if (c.is_eligible) return null;
  if (c.next_rank_id === null) {
    return 'No tiene siguiente grado en su sistema: ya está arriba, o no tiene sistema asignado.';
  }

  const partes: string[] = [];
  // 0,8 es el requisito más habitual; la cifra exacta la impone cada cinturón.
  if (c.attendance_rate < 0.8) {
    partes.push(
      `viene al ${Math.round(c.attendance_rate * 100)} % de las clases`
    );
  }
  if (!c.is_first_exam && c.months_since_last < 3) {
    partes.push(
      c.months_since_last === 0
        ? 'se graduó este mes'
        : `hace ${c.months_since_last} ${c.months_since_last === 1 ? 'mes' : 'meses'} del último grado`
    );
  }

  return partes.length > 0
    ? `Le falta: ${partes.join(' y ')}.`
    : 'No cumple los requisitos de su cinturón.';
}

/** El nombre de un candidato o inscrito, sin el hueco del apellido que falta. */
export const nombreDeCandidato = (c: {
  first_name: string;
  last_name: string | null;
}): string => [c.first_name, c.last_name].filter(Boolean).join(' ');

/**
 * El `eligibility_snapshot`, en líneas legibles.
 *
 * Es **carga opaca**: no se filtra por su contenido y su forma puede cambiar sin
 * avisar al panel. Así que esto no lo interpreta — lo aplana a `clave: valor` y
 * traduce las claves que conoce. Una clave nueva sale con su nombre crudo, que es
 * exactamente lo que hace falta de un registro histórico: enseñarlo, no juzgarlo.
 *
 * ## Baja **un nivel**, y eso no es un detalle
 *
 * La forma real que devuelve el servidor mete los requisitos en un objeto anidado:
 *
 * ```json
 * { "is_eligible": true, "attendance_rate": 0.875,
 *   "required": { "min_classes": 20, "min_attendance_rate": 0.6 } }
 * ```
 *
 * La primera versión de esta función descartaba los objetos —`typeof v !== 'object'`—
 * y con ellos **todo el bloque `required`**: es decir, tiraba justo *los requisitos
 * tal como estaban al inscribir*, que es literalmente lo único para lo que existe esta
 * columna. Se vio mirando la respuesta de verdad, no la idea de la respuesta.
 *
 * Baja un nivel y no más: es carga opaca y aplanar en profundidad convertiría un
 * objeto raro en cuarenta líneas ilegibles. Un nivel cubre la forma que hay hoy y
 * cualquier otra degrada a «se enseña lo de arriba».
 */
const CLAVES: Record<string, string> = {
  // Lo que tenía el alumno.
  attendance_rate: 'Asistencia que tenía',
  months_since_last: 'Meses desde el último grado',
  classes_attended: 'Clases asistidas',
  age: 'Edad',
  is_first_exam: 'Era su primer examen',
  is_eligible: 'Cumplía los requisitos',
  taken_at: 'Calculado el',
  // Lo que se le pedía, del bloque `required`.
  min_attendance_rate: 'Asistencia que se pedía',
  min_months_since_last: 'Meses que se pedían',
  min_classes: 'Clases que se pedían',
  min_age: 'Edad mínima',
};

/** Una tasa se lee mejor en porcentaje: 0,875 es «88 %». */
const esTasa = (clave: string): boolean => clave.includes('rate');

function comoTexto(clave: string, valor: unknown): string {
  if (typeof valor === 'boolean') return valor ? 'sí' : 'no';
  if (typeof valor === 'number' && esTasa(clave)) {
    return `${Math.round(valor * 100)} %`;
  }
  // Un instante, venga como texto o como `Date`. Ver la nota de Eden abajo.
  if (valor instanceof Date || clave === 'taken_at') {
    return fechaYHora(valor as Date | string);
  }
  return String(valor);
}

export function resumenDeRequisitos(
  snapshot: Readonly<Record<string, unknown>> | null
): readonly { clave: string; valor: string }[] {
  if (!snapshot) return [];

  const filas: { clave: string; valor: string }[] = [];

  const meter = (k: string, v: unknown) => {
    if (v === null || v === undefined) return;
    filas.push({ clave: CLAVES[k] ?? k, valor: comoTexto(k, v) });
  };

  for (const [k, v] of Object.entries(snapshot)) {
    /*
     * **Un `Date` es un valor, no un contenedor**, y esto no es teoría: Eden
     * deserializa los instantes a `Date`, así que `taken_at` llega como objeto. Con
     * un `typeof v === 'object'` a secas se iba por la rama de aplanado, y
     * `Object.entries(new Date())` está **vacío**: la fecha desaparecía sin dejar
     * rastro ni error. Es la misma trampa de Eden de siempre, disfrazada.
     */
    const contenedor =
      v !== null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      !(v instanceof Date);

    if (contenedor) {
      // Un nivel: aquí vive `required`, que es la mitad que importa.
      for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) {
        meter(k2, v2);
      }
    } else {
      meter(k, v);
    }
  }

  return filas;
}

/**
 * La nota final de un criterio, como porcentaje del máximo ponderado.
 *
 * **No es una suma**, y esa es la decisión del contrato: «así el listón no se mueve
 * solo al añadir un criterio». Se calcula aquí solo para poder explicarlo en la
 * pantalla de la rúbrica; la nota de verdad la pone el servidor al calificar.
 */
export function maximoPonderado(
  criterios: readonly { max_score: number; weight: number }[]
): number {
  return criterios.reduce((s, c) => s + c.max_score * c.weight, 0);
}

/** Una fila de la rúbrica mientras se edita, antes de mandarla. */
export interface FilaDeRubrica {
  /** Para la `key` de React: el servidor asigna el suyo al guardar. */
  readonly clave: string;
  readonly belt_rank_id: string | null;
  readonly code: string;
  readonly label: string;
  readonly category: string;
  readonly max_score: string;
  readonly weight: string;
}

export const filaVacia = (beltRankId: string | null): FilaDeRubrica => ({
  clave: crypto.randomUUID(),
  belt_rank_id: beltRankId,
  code: '',
  label: '',
  category: '',
  // Diez y uno: el reparto más común, y el que menos explicación necesita.
  max_score: '10',
  weight: '1',
});

export const filaDesde = (c: CriterioDeExamen): FilaDeRubrica => ({
  clave: c.id,
  belt_rank_id: c.belt_rank_id,
  code: c.code,
  label: c.label,
  category: c.category ?? '',
  max_score: String(c.max_score),
  weight: String(c.weight),
});

/**
 * Qué le falta a una fila para poder guardarse.
 *
 * `null` si está bien. Se comprueba aquí y no al recibir el `422` porque el `PUT`
 * **sustituye la rúbrica entera**: un error a mitad de la lista tira las sesenta
 * filas, y descubrirlo al pulsar «guardar» con una tabla llena es perder el trabajo.
 */
export function faltaEnLaFila(f: FilaDeRubrica): string | null {
  if (f.code.trim() === '') return 'Falta el código.';
  if (f.label.trim() === '') return 'Falta el nombre del criterio.';

  const maximo = Number(f.max_score.replace(',', '.'));
  const peso = Number(f.weight.replace(',', '.'));
  if (!Number.isFinite(maximo) || maximo <= 0) {
    return 'El máximo tiene que ser un número mayor que cero.';
  }
  if (!Number.isFinite(peso) || peso <= 0) {
    return 'El peso tiene que ser un número mayor que cero.';
  }
  return null;
}

/** Una fila lista para el `PUT`. Devuelve `null` si no se puede convertir. */
export function aCriterio(f: FilaDeRubrica) {
  if (faltaEnLaFila(f) !== null) return null;
  return {
    belt_rank_id: f.belt_rank_id,
    code: f.code.trim(),
    label: f.label.trim(),
    category: f.category.trim() === '' ? null : f.category.trim(),
    max_score: Number(f.max_score.replace(',', '.')),
    weight: Number(f.weight.replace(',', '.')),
  };
}
